import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
// `deleteField` from the facade, not the SDK: it has to reach the offline
// queue as a transform rather than as an SDK sentinel the codec cannot read.
// `updateEntry` below uses it to clear a field on a document that keeps the
// rest of itself (see `docShape.js`).
import { deleteField, hasLiveWriteTo, writeBatch } from './writes';
import { docRef, COLLECTIONS, SETTINGS_DOCS } from './paths';
import { db, storage } from '../lib/firebase';
import { ref as storageRef, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { callable } from './useEntitlement';
import { createFileLinks, linksFromCallable } from './fileLinks';
import {
  FileRefused, UPLOAD_FAILED, refuseFile, uploadErrorMessage, uploadType,
  warnUnsweptReceipts,
} from '../lib/fileLimits';
import { useLedger } from './useLedger';
import { useEvents } from './useEvents';
import { useStudents } from './useStudents';
import { useAttendance } from './useAttendance';
import { useSettings } from './useSettings';
import { useCollectionWindow } from './useCollectionWindow';
import { absentAtServer, serverUnreachable } from './serverConfirmed';
import { UploadCancelled, watchUpload } from '../lib/uploadWatch';

// What each write *is* — an entry, an edit, an imported row, the category
// seed, a rename's and a delete's writes, a receipt row, an edit's
// reconciliation of receipts — is `src/domain/expenses.js`'s. This provider
// keeps the batches, the ids and the bucket.
import { financialSummary, monthlySeries, categoryBreakdown, monthBounds,
         expenseInstancesBetween, expenseInstancesReport, categoriesFor,
         categoryRecords, categoryUsage, attachmentsFor,
         attachmentIdForPath, entryIdPrefix, expenseEntryDocument,
         expenseEntryUpdateDocument, importedEntryDocument, categorySeedWrites,
         newCategoryDocument, categoryRenamePlan, categoryRemovalPlan,
         mileageCategoryAfterRename, mileageCategoryClearedByRemoval,
         storableAttachments, attachmentRowDocument, attachmentReconciliation,
         entryRemovalPlan, CATEGORY_SEED_MARKER } from '../domain/expenses';
import { freeId } from '../domain/freeId';
import { todayISO } from '../domain/clock';
import { projectTeaching } from '../domain/teaching';

// Expenses and other revenue.
//
// Teaching income is not stored here — it is folded from the money ledger, so
// the two can never disagree about what a lesson earned. This collection holds
// only what the ledger does not already know: costs, and income that did not
// come from a lesson.

const ExpensesContext = createContext(null);
const TRACK_LIMIT = 5000;

// How many receipt rows one studio's snapshot holds (#183).
//
// **5000, because `withinListBound` in firestore.rules refuses a `list` asking
// for more** — `request.query.limit <= 5000` — and a window over that is not a
// truncated read, it is a `PERMISSION_DENIED` on every page load with no
// receipt anywhere in the app.
//
// The same ceiling as `TRACK_LIMIT` above, so a studio with 5,000 entries can
// have fewer than one receipt each before this window starts losing the later
// rows. `attachmentsFor` is a filter over it, so such a studio loses the cards
// on its oldest expenses, as `useFamilies`' contacts window does.
const ATTACHMENTS_LIMIT = 5000;

// Taking a receipt object out of the bucket, best-effort, in one place.
//
// `removeAttachment` below is the form's sweep calling in; `removeEntry` is the
// row going with its receipt. One function, so both can tell a refused delete
// from a successful one (#119, #140) — deleting an entry is where an unswept
// object is least recoverable, because the row that named its path is going.
//
// Module scope, because it needs nothing from the component: `storage` is a
// module import.
//
// Never throws, for the reason each caller gives below. Resolves with the error
// it swallowed when the bucket refused, and with null when the object went or
// was already gone — "already gone" being a success and the correct arithmetic
// besides, since its bytes came off `bytesHeld` when it went.
async function deleteReceiptObject(path) {
  if (!path) return null;
  try {
    await deleteObject(storageRef(storage, path));
    return null;
  } catch (err) {
    if (err?.code === 'storage/object-not-found') return null;
    console.warn('[expenses] receipt delete failed', err);
    return err;
  }
}

// ── The seed asks the server first (#631) ───────────────────────────────────
//
// The built-ins become documents the first time the manager is opened. The
// seed is eleven whole-document sets and the marker, so a seed written over a
// studio that has already been seeded would put every renamed built-in's
// default label back (leaving its relabelled entries under a name the list no
// longer has) and bring back every deleted one. A device's own read cannot
// decide it: `customCategories` is `[]` until the window has spoken, and a
// cold-cache first answer can be `[]` too.
//
// So it is #620's shape (`recordStudioTimeZone`, and ./serverConfirmed.js):
// the marker is written only when **the server** says there is none, and each
// built-in only when the server says that document is absent too — so a
// studio with built-ins and no marker keeps what it has.
//
// **Offline is "unknown", not a failure (#681).** The server read refuses
// outright while the SDK thinks it is offline (`serverUnreachable`), which
// includes a moment after a transient transport error. An unconfirmed absence
// is never seeded on, so nothing is written and nothing is said; the next open
// of the manager asks again. Until then the built-ins are listed from the
// constants and are not editable, and an Edit or Delete on one says why
// (`BUILT_IN_NOT_SEEDED`).
//
// Resolves true when it wrote the seed; false when it wrote nothing, because
// the server holds the marker or because the server could not be reached.
// Rejects on any other refusal of the read.
export async function seedExpenseCategories(uid) {
  if (!db || !uid) return false;
  const writes = categorySeedWrites(serverTimestamp());
  const pathOf = (id) => docRef(uid, COLLECTIONS.expenseCategories, id).path;
  const refs = writes.map(({ id }) => docRef(uid, COLLECTIONS.expenseCategories, id));
  const absent = await absentAtServer(refs).then(
    (paths) => paths,
    (err) => {
      if (!serverUnreachable(err)) throw err;
      console.info('[expense categories] the server could not be reached, so whether the built-in '
        + 'categories are saved is not known and nothing was written; the next visit asks again:', err);
      return null;
    },
  );
  if (!absent || !absent.has(pathOf(CATEGORY_SEED_MARKER))) return false;
  const batch = writeBatch(db);
  for (const { id, document } of writes) {
    // The ref built in the call, as test/core/wholeDocumentSets.test.js
    // needs to attribute this create to its collection.
    if (absent.has(pathOf(id))) batch.set(docRef(uid, COLLECTIONS.expenseCategories, id), document);
  }
  await batch.commit();
  return true;
}

// What a rename or delete of a built-in that is not a document yet answers
// with: the seed has not reached the server, so there is nothing to rename
// and a delete would be undone by the seed when it lands.
export const BUILT_IN_NOT_SEEDED = 'The built-in categories can be renamed or deleted once '
  + 'this device has reached the server. Check your connection and open this page again.';

export function ExpensesProvider({ children }) {
  const { user } = useAuth();
  const { entries: ledgerEntries, loading: ledgerLoading } = useLedger();
  const { occurrencesBetween, loading: eventsLoading } = useEvents();
  const { byId: studentById } = useStudents();
  // Only for `projectTeachingFor` below. AttendanceProvider wraps this one in
  // App.jsx, which is what makes the hook legal here.
  const { statusFor, loading: attendanceLoading } = useAttendance();
  // Only for the Mileage Log's `expenseCategory` setting, which `renameCategory`
  // and `removeCategory` carry along below (#555). SettingsProvider wraps this
  // one in App.jsx too, which is what makes this hook legal here.
  const { settings } = useSettings();
  // The Region's decimal separator, for the sizes in an attachment's refusal
  // (#583). LocaleBridge wraps this provider in App.jsx.
  const { decimalSeparator } = useLocale();
  // Two windows, two calls (#207 U5). `expenseCategories` never asked for
  // metadata changes; `expenseEntries` did, and is the one that drives `loading`.
  const categoriesWindow = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.expenseCategories,
    limit: 200,
    withMetadata: false,
  });
  const entriesWindow = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.expenseEntries,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  // The receipts on those entries (#183). One document per attachment, so this
  // is a third window rather than a field on the second.
  //
  // Its own constant even though it holds the same number as `TRACK_LIMIT`:
  // `TRACK_LIMIT` is a judgement about how many expenses fit in a snapshot,
  // and `ATTACHMENTS_LIMIT` is the ceiling `firestore.rules` imposes.
  //
  // `attachmentsFor` is a filter over one window rather than a per-entry
  // query, so no listener opens and closes as a teacher scrolls the list.
  const attachmentsWindow = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.expenseAttachments,
    limit: ATTACHMENTS_LIMIT,
  });
  const attachments = attachmentsWindow.docs;
  const customCategories = categoriesWindow.docs;
  // Newest first. Copied before sorting: the hook's array is its own memoised
  // value, not this provider's to reorder.
  const entries = useMemo(
    () => [...entriesWindow.docs].sort((a, b) => (a.occurredOn < b.occurredOn ? 1 : -1)),
    [entriesWindow.docs],
  );
  const loading = entriesWindow.loading;

  // Forecast teaching income for a month that has not happened yet: every
  // scheduled occurrence priced as if it will be taught and billed.
  //
  // **The fold is the core's** (`teaching::project_teaching` in
  // core/src/teaching.rs, #470), through `projectTeaching` in
  // domain/teaching.js; all three clients ask the one fold. What it decides:
  //
  //   * both ways of calling a lesson off pull the number down (#159) — a
  //     deleted entry never reaches the fold, and one marked off through the
  //     attendance dropdown is gated by `is_billable_for` with the
  //     occurrence's make-up flag (#362), the same gate the Day view's
  //     "billed today" tile asks;
  //   * a lesson's pricing rule first — `none` is free, `fixed` its stamped
  //     price — as `useEvents.priceFor` reads it;
  //   * a fixed-monthly student counted once at their rate, ahead of the
  //     billable gate, recognised by the *mode* and not by a price coming back
  //     empty (#322: an hourly student on an all-day event prices as nothing
  //     too, and is not a monthly fee).
  const projectTeachingFor = useCallback((yearMonth) => {
    const { start, end } = monthBounds(yearMonth);
    return projectTeaching(occurrencesBetween(start, end), studentById, statusFor);
  }, [occurrencesBetween, studentById, statusFor]);

  const seeded = customCategories.some((c) => c.id === CATEGORY_SEED_MARKER);
  // `renameCategory` and `removeCategory` below refuse a row the list shows
  // from the constants rather than from a document — a built-in before the
  // seed has reached this device (#631). Renaming one would merge a lone label
  // into a document that does not exist, and deleting one deletes nothing that
  // the seed would not bring back. They answer `BUILT_IN_NOT_SEEDED`.

  // The studio's receipt links, held for as long as they are good (#431;
  // src/data/fileLinks.js). One cache per signed-in studio: a new uid is a new
  // cache, so no link minted for one studio is ever handed to another.
  const uid = user?.uid || null;
  const links = useMemo(
    () => createFileLinks({ fetchLinks: linksFromCallable(uid ? callable('fileLinks') : null) }),
    [uid],
  );

  const value = useMemo(() => ({
    loading,
    // The entries' read stopped before it answered (#647).
    failed: entriesWindow.failed,
    // The month's summary and trend are folded from the entries, the ledger
    // and the priced schedule: they are read only once all four have answered
    // (#629). Until then the panel shows the dash, not "$0.00" and "No income
    // or expenses recorded in this period yet."
    summaryRead: !loading && !ledgerLoading && !eventsLoading && !attendanceLoading,
    entries,
    customCategories,
    categoriesFor: (type) => categoriesFor(type, customCategories, seeded),
    categoryRecords: () => categoryRecords(customCategories, seeded),
    categoryUsage: () => categoryUsage(entries),
    categoriesSeeded: seeded,
    // Whether the categories window has answered at all. Not a server
    // confirmation (a cold cache answers too); it only spares a studio whose
    // read already shows the marker the server round trip.
    categoriesRead: categoriesWindow.hasRead,

    // Turn the built-in categories into real documents, so the manager can
    // rename and delete them like any other. One batch, deterministic ids: two
    // devices doing this at the same time converge on eleven rows. Only
    // against the server's answer — `seedExpenseCategories` above.
    seedCategories: async () => {
      if (!user || seeded) return false;
      return seedExpenseCategories(user.uid);
    },

    // ── Neither of these checks for a duplicate name ─────────────────────
    //
    // The refusal lives in the two editors — `expenseCategoryErrors` in
    // domain/expenses.js, wired into pages/ExpenseCategoriesPage.jsx and the
    // "+ Add Category" drawer on pages/ExpensesPage.jsx (#144) — where it can
    // say *why* to the person who typed the name; a provider can only throw or
    // silently drop.
    //
    // These two and `seedCategories` above are the only writers of this
    // collection: `resolveImportCategory` matches against the studio's list and
    // refuses a row rather than inventing one (#134).
    addCategory: async ({ type, label }) => {
      const document = user ? newCategoryDocument({ type, label }, serverTimestamp()) : null;
      if (!document) return null;
      const id = freeId('ecat');
      const batch = writeBatch(db);
      batch.set(docRef(user.uid, COLLECTIONS.expenseCategories, id), document);
      await batch.commit();
      return document.label;
    },

    // Renaming carries the entries with it. The category is stored on each
    // entry by label, so leaving them behind would orphan every row under a
    // name that no longer exists in the list.
    //
    // So the name it is renamed *to* has to be refused if the studio already
    // has it (`expenseCategoryErrors`): this loop would relabel every entry
    // under `from` to a label another category owns, merging the two sets.
    //
    // It also carries the Mileage Log's `expenseCategory` setting, in the same
    // batch (#555): that setting names a category by the same label an entry's
    // `category` does. `mileageCategoryAfterRename` is null for every rename
    // except the one that touches the category the Mileage tab has chosen.
    renameCategory: async ({ id, type, from, to }) => {
      if (user && !seeded && !customCategories.some((c) => c.id === id)) return BUILT_IN_NOT_SEEDED;
      const plan = user ? categoryRenamePlan({ type, from, to }, entries) : null;
      if (!plan) return;
      const batch = writeBatch(db);
      batch.set(docRef(user.uid, COLLECTIONS.expenseCategories, id), plan.document, { merge: true });
      for (const entryId of plan.entryIds) {
        batch.set(docRef(user.uid, COLLECTIONS.expenseEntries, entryId), plan.entryDocument, { merge: true });
      }
      const mileageLabel = mileageCategoryAfterRename({ type, from, to }, settings.mileage?.expenseCategory);
      if (mileageLabel != null) {
        // A real nested object, not a dotted top-level key: `merge: true`
        // deep-merges a nested map (as `studioFieldsToWrite` relies on), so
        // this leaves `autoExpenses` and `rateTenThousandths` alone. A literal
        // `'mileage.expenseCategory'` key would be stored as one field whose
        // name contains a dot.
        batch.set(
          docRef(user.uid, COLLECTIONS.settings, SETTINGS_DOCS.studio),
          { mileage: { expenseCategory: mileageLabel } },
          { merge: true },
        );
      }
      await batch.commit();
    },

    // Entries keep their history rather than following the category into the
    // bin: they fall back to "Not Specified", which is a state the summaries
    // already handle, instead of vanishing from the books.
    //
    // The same cascade as `renameCategory`, for the same reason (#555): a
    // deleted category still named by the Mileage tab's setting is a label
    // with nothing behind it, so a delete that matches it clears the setting
    // rather than leaving it stale.
    removeCategory: async ({ id, type, label, isSystem = false }) => {
      if (!user) return null;
      if (!seeded && !customCategories.some((c) => c.id === id)) return BUILT_IN_NOT_SEEDED;
      const plan = categoryRemovalPlan({ type, label, isSystem }, entries);
      // The category family payments file into is kept whatever asked for
      // the delete; the refusal goes back to the caller.
      if (plan.refusal) return plan.refusal;
      const batch = writeBatch(db);
      batch.delete(docRef(user.uid, COLLECTIONS.expenseCategories, id));
      for (const entryId of plan.entryIds) {
        batch.set(docRef(user.uid, COLLECTIONS.expenseEntries, entryId), plan.entryDocument, { merge: true });
      }
      if (mileageCategoryClearedByRemoval({ type, label }, settings.mileage?.expenseCategory)) {
        batch.set(
          docRef(user.uid, COLLECTIONS.settings, SETTINGS_DOCS.studio),
          { mileage: { expenseCategory: null } },
          { merge: true },
        );
      }
      await batch.commit();
    },

    // Entries falling in an arbitrary range, for the list's own date pickers —
    // MMS scopes the list separately from the summary month.
    between: (startISO, endISO) => expenseInstancesBetween(entries, startISO, endISO),
    // The list needs to know when it is showing a truncated expansion.
    betweenReport: (startISO, endISO) => expenseInstancesReport(entries, startISO, endISO),
    forMonth: (yearMonth) => {
      const { start, end } = monthBounds(yearMonth);
      return expenseInstancesBetween(entries, start, end);
    },
    summaryFor: (yearMonth, basis) =>
      financialSummary({ ledgerEntries, expenseEntries: entries, yearMonth, basis }),
    seriesFor: (yearMonth, basis, opts = {}) =>
      monthlySeries({
        ledgerEntries,
        expenseEntries: entries,
        yearMonth,
        basis,
        // The studio's month, not UTC's (#542).
        currentMonth: todayISO().slice(0, 7),
        projectTeaching: projectTeachingFor,
        ...opts,
      }),
    breakdownFor: (yearMonth, type) => {
      const { start, end } = monthBounds(yearMonth);
      return categoryBreakdown(entries, start, end, type);
    },

    addEntry: async (input) => {
      if (!user) return null;
      const id = freeId(entryIdPrefix(input.type));
      const batch = writeBatch(db);
      // `expenseEntryDocument` is the backstop, not the refusal (#166): the
      // refusal with a sentence is `entryErrors`, in front of this.
      batch.set(docRef(user.uid, COLLECTIONS.expenseEntries, id), expenseEntryDocument(input, serverTimestamp()));
      // The receipts, one document each, **in the same batch as the entry**
      // (#183): an expense and its receipts land together or not at all, and a
      // batch commits offline.
      //
      // The bound on the batch is 500 writes and `EXPENSE_ATTACHMENTS_MAX` is
      // ten, so one entry's receipts cannot approach it.
      for (const attachment of storableAttachments(input.attachments, id)) {
        batch.set(
          docRef(user.uid, COLLECTIONS.expenseAttachments, attachmentIdForPath(attachment.path)),
          attachmentRowDocument(attachment, serverTimestamp()),
        );
      }
      await batch.commit();
      return id;
    },

    // Editing an instance of a recurring rule edits the rule, for the same
    // reason deleting one does: the instances are derived and have no documents
    // of their own to carry a divergent value.
    updateEntry: async (id, fields) => {
      if (!user) return;

      // The receipt rows this device has already queued and not yet read back
      // count as existing: a second Save before the first one's rows arrive
      // must not create them again. Asked of the outbox per row
      // the reconciliation would create — the question `useSettings` asks of
      // the studio document, for the same reason.
      const reconcile = (queued) => attachmentReconciliation({
        attachments: fields.attachments,
        entryId: id,
        rows: attachments,
        entry: entries.find((e) => e.id === id),
        queued,
      });
      const candidates = reconcile([]).creates;
      const live = await Promise.all(candidates.map(({ id: rowId }) => hasLiveWriteTo(
        docRef(user.uid, COLLECTIONS.expenseAttachments, rowId).path,
      )));
      const plan = reconcile(candidates.filter((_, i) => live[i]).map(({ id: rowId }) => rowId));

      const batch = writeBatch(db);
      batch.set(docRef(user.uid, COLLECTIONS.expenseEntries, id), expenseEntryUpdateDocument(fields), { merge: true });

      // The receipts, reconciled rather than rewritten (#183) — and an edit
      // migrates the entry it is editing, stripping the inline fields in the
      // same batch. `attachmentReconciliation` has the argument for both.
      if (plan.strip.length) {
        batch.set(
          docRef(user.uid, COLLECTIONS.expenseEntries, id),
          Object.fromEntries(plan.strip.map((f) => [f, deleteField()])),
          { merge: true },
        );
      }
      for (const rowId of plan.deletes) {
        batch.delete(docRef(user.uid, COLLECTIONS.expenseAttachments, rowId));
      }
      for (const { id: rowId, attachment } of plan.creates) {
        batch.set(
          docRef(user.uid, COLLECTIONS.expenseAttachments, rowId),
          attachmentRowDocument(attachment, serverTimestamp()),
        );
      }
      await batch.commit();
    },

    // `onProgress({ transferred, total })` as the bytes go up, under the
    // no-progress watchdog (src/lib/uploadWatch.js, #612). Aborting `signal`
    // cancels it (`UploadCancelled`); bytes that land anyway are deleted,
    // since nothing will name them.
    uploadAttachment: async (file, { onProgress = null, signal = null } = {}) => {
      if (!user || !file) return null;
      if (!storage) throw new FileRefused(UPLOAD_FAILED);

      // Before any bytes move, and before the round trip. The server refuses
      // the same two things and storage.rules refuses them again at the object;
      // this exists so a phone connection is not spent finding out.
      //
      // `refuseFile`, not a receipt-shaped version of it: a receipt takes the
      // studio file service's cap and type list (#96), so all three gates
      // measure against the same two numbers.
      const refusal = refuseFile(file, decimalSeparator);
      if (refusal) throw new FileRefused(refusal);

      const authorizeUpload = callable('authorizeUpload');
      if (!authorizeUpload) throw new FileRefused('Receipt uploads are not switched on yet.');

      // The content type as the rule will see it, decided once and used for
      // both the authorisation and the object, so the callable and the bucket
      // judge the same type. `application/octet-stream` is a type the rule does
      // not allow.
      const contentType = uploadType(file);

      let grant;
      try {
        const res = await authorizeUpload({
          purpose: 'receipt', name: file.name || '', contentType, size: file.size,
        });
        grant = res?.data;
      } catch (err) {
        throw new FileRefused(uploadErrorMessage(err));
      }
      if (!grant?.path) throw new FileRefused(UPLOAD_FAILED);

      // Resumable (#612), as the library's upload is (#577): it reports
      // progress, which the receipt's row shows, and a dropped connection
      // resumes a chunk rather than the file.
      if (signal?.aborted) throw new UploadCancelled();
      const task = uploadBytesResumable(storageRef(storage, grant.path), file, { contentType });
      await watchUpload(task, { total: file.size || 0, onProgress, signal });
      if (signal?.aborted) {
        await deleteObject(storageRef(storage, grant.path)).then(null, (err) => {
          console.warn('[expenses] a cancelled receipt landed and could not be deleted', err);
        });
        throw new UploadCancelled();
      }

      return {
        path: grant.path,
        // The teacher's own filename, kept for display; the object does not
        // carry it. Cut to what the rule takes by `storableAttachments` on the
        // way into the document rather than here, so the drawer shows the file
        // the teacher picked (#104).
        name: file.name || 'receipt',
        contentType,
        size: file.size || 0,
      };
    },

    // A link for viewing, and one that expires (#431).
    //
    // A signed link the `fileLinks` callable mints for this studio only, good
    // for thirty days from the end of the UTC day it was minted in (#497), with
    // the expiry in the URL itself (functions/files/links.js says why). Not
    // `getDownloadURL`, whose token URL never expires and is honoured for
    // anyone holding it. `links` holds each one until it is inside its renewal
    // margin, so a second open of the same receipt makes no round trip.
    //
    // Fetched on demand rather than stored on the entry: no document holds a
    // URL.
    attachmentUrl: (path) => links.linkFor(path),

    // Deleting the object is best-effort. If it fails the entry still goes, and
    // an orphaned image costs pennies — whereas refusing to delete the entry
    // because its receipt could not be removed would strand the row.
    //
    // ── What the delete does to the byte counter (#62) ───────────────────────
    //
    // Nothing here. The object delete fires `onFileDeleted`, which takes the
    // object's *actual* size off `bytesHeld` — a teacher's delete, the sweep's
    // and a lifecycle rule's alike — so decrementing from here as well would
    // double it. An object we could not delete stays charged against the
    // studio's ceiling with the row that named it gone; refusing to delete a
    // teacher's expense over a storage error would be the worse trade.
    //
    // ── Who calls this, and when (#94) ───────────────────────────────────────
    //
    // AttachmentField's "Remove" (`onChange({ attachment: null })` in
    // EntryFormDrawer) clears the *form field* and nothing else; deleting on
    // the press would destroy a saved entry's evidence if the teacher then
    // cancelled the form. The caller is `useExpenseEntryForm`, which compares
    // what the entry had against what it has now — at save, and again when the
    // form is abandoned — and passes the difference here. `orphanedReceipts` in
    // lib/fileLimits.js is that comparison.
    //
    // ── What it reports back (#119) ──────────────────────────────────────────
    //
    // It never throws. It resolves with the error it swallowed when the delete
    // was refused, and with null when the object went **or was already gone**
    // — the state the caller wanted, whose bytes came off `bytesHeld` when it
    // went. Null rather than a boolean, so callers and test fakes that ignore
    // the result read as success; the receipt sweep in `useExpenseEntryForm`
    // names the paths it could not take away (see `sweepReceipts`).
    //
    // The body is `deleteReceiptObject` at the top of this file, shared with
    // `removeEntry` below (#140).
    removeAttachment: (path) => deleteReceiptObject(path),

    // The receipts on one entry, in the order they were attached (#183).
    //
    // A filter over the one window rather than a per-entry query, as
    // `contactsFor` in useFamilies.jsx is: a listener per expense would open
    // and close as a teacher scrolls the list.
    //
    // **Falls back to the entry's inline attachments** for a row
    // `scripts/migrate-expense-attachments.mjs` has not reached, whose
    // receipts would otherwise vanish from the screen. `inlineAttachmentsOf`
    // is the one place that knows about them.
    //
    // The collection wins whenever it has a row, so a migrated entry never
    // shows the stale inline copy beside the live one.
    attachmentsFor: (entryId) => attachmentsFor(attachments, entries, entryId),
    // Whether the receipts window has answered yet: `attachmentsFor` returns
    // none both for a row whose receipts have not arrived and for a row that
    // has none. `contactsLoading` in useFamilies.jsx is the same value.
    attachmentsLoading: attachmentsWindow.loading,

    // Bulk import. Chunked because a Firestore batch caps at 500 writes, and an
    // import of years of bookkeeping passes that.
    //
    // Fresh ids rather than deterministic ones: two devices importing the same
    // spreadsheet are two decisions to import it, and there is nothing in a CSV
    // row that identifies it as the same transaction rather than a genuine
    // duplicate the teacher recorded twice.
    importEntries: async (drafts) => {
      if (!user || !drafts.length) return 0;
      const CHUNK = 400;
      let written = 0;
      for (let i = 0; i < drafts.length; i += CHUNK) {
        const batch = writeBatch(db);
        for (const d of drafts.slice(i, i + CHUNK)) {
          const id = freeId(entryIdPrefix(d.type));
          batch.set(docRef(user.uid, COLLECTIONS.expenseEntries, id), importedEntryDocument(d, serverTimestamp()));
          written += 1;
        }
        await batch.commit();
      }
      return written;
    },

    removeEntry: async (id) => {
      if (!user) return;
      // Take the receipts with the entry, or the bucket keeps images, and
      // bytes charged against the ceiling (#62), for an expense that no longer
      // exists. `onFileDeleted` does the giving back (see removeAttachment
      // above).
      //
      // **After the row is written, and it never throws**, as the form's sweep:
      // an object taken away before a delete that then fails would leave a row
      // naming a path with nothing behind it.
      //
      // The refusal is *said* (#140): the row that named this path is going, so
      // after this nothing in the studio knows the object exists. See
      // `warnUnsweptReceipts` in lib/fileLimits.js.
      //
      // **Every receipt (#104), in both places one can be recorded (#183):** an
      // `expenseAttachments` row, or the inline list and the singular
      // `attachment` an unmigrated entry carries. `attachmentPaths` reads
      // either.
      //
      // The rows cascade **in the same batch as the entry**, as `useFamilies`
      // cascades a family's contacts: a row whose `entryId` names nothing is
      // unreachable from every screen, and one batch cannot half-apply.
      // `EXPENSE_ATTACHMENTS_MAX` per entry is far below a batch's 500.
      //
      // The loop does not stop on a refusal, as `sweepReceipts` does not.
      // Sequential so the warning names them in the order the row held them,
      // and a handful of deletes is not a burst.
      const { rowIds, paths } = entryRemovalPlan(id, attachments, entries.find((e) => e.id === id));
      const batch = writeBatch(db);
      for (const rowId of rowIds) {
        batch.delete(docRef(user.uid, COLLECTIONS.expenseAttachments, rowId));
      }
      batch.delete(docRef(user.uid, COLLECTIONS.expenseEntries, id));
      await batch.commit();
      const unswept = [];
      for (const path of paths) {
        if (await deleteReceiptObject(path)) unswept.push(path);
      }
      warnUnsweptReceipts(unswept);
    },
  }), [
    entries, entriesWindow.failed, attachments, attachmentsWindow.loading, customCategories, seeded,
    ledgerEntries, loading, ledgerLoading, eventsLoading, attendanceLoading, user, projectTeachingFor, links, settings.mileage?.expenseCategory, decimalSeparator,
  ]);

  return <ExpensesContext.Provider value={value}>{children}</ExpensesContext.Provider>;
}

export function useExpenses() {
  const ctx = useContext(ExpensesContext);
  if (!ctx) throw new Error('useExpenses must be used within an ExpensesProvider');
  return ctx;
}
